import React, { useCallback, useMemo, useState } from "react";
import sortBy from "lodash/sortBy";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@apollo/client/react";

import useMutation from "../../../hooks/useMutation";
import useAuth from "../../../hooks/useAuth";
import EntitySelect from "../../EntitySelect";
import useNotification from "../../../hooks/notification";
import { BULK_TRANSFER_PROJECT_OPTIONS_QUERY } from "../../../constants/graphql/queries";
import { ASSETS_BULK_TRANSFER_MUTATION } from "../../../constants/graphql/mutations";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "../../../utils/projectOptionLabels";

const getActiveProjects = (projects = []) => {
  const activeProjects = projects.filter(project => project.active);
  return sortBy(activeProjects, project => project.name?.toLowerCase() || "");
};

export default function BulkTransferModal({ selectedIds = [], onClose, onComplete }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);
  const { notifySuccess, notifyError } = useNotification();
  const [projectId, setProjectId] = useState(null);
  const [assetsBulkTransfer] = useMutation(ASSETS_BULK_TRANSFER_MUTATION);
  const { data: { projects = [] } = {}, loading } = useQuery(BULK_TRANSFER_PROJECT_OPTIONS_QUERY);

  const projectList = useMemo(() => getActiveProjects(projects), [projects]);
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );

  const handleTransferAssets = useCallback(() => {
    assetsBulkTransfer({
      variables: {
        assetIds: selectedIds,
        projectId,
      },
      onSuccess: () => {
        notifySuccess(
          `Successfully transferred ${selectedIds.length} asset${selectedIds.length > 1 ? "s" : ""}`
        );
        if (onComplete) onComplete();
        onClose();
      },
      onFailure: () => notifyError("Something went wrong"),
    });
  }, [assetsBulkTransfer, selectedIds, projectId, notifySuccess, notifyError, onComplete, onClose]);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6, position: "relative" }}>
        <Typography fontSize={22} fontWeight={500} color="text.primary" align="center">
          Transfer Items
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box pt={1}>
          {loading ? (
            <Skeleton variant="rounded" height={40} />
          ) : (
            <EntitySelect
              fullWidth
              label={`Select New ${settings.filter_field2}`}
              value={projectId}
              suggestions={projectList}
              getOptionLabel={option =>
                getProjectOptionLabel(
                  option,
                  duplicateNameCounts,
                  showClientNameInProjectList
                )
              }
              renderOption={(props, option) => {
                const optionProps = { ...props };
                delete optionProps.key;
                const clientLabel = getProjectClientLabel(
                  option,
                  duplicateNameCounts,
                  showClientNameInProjectList
                );
                return (
                  <li {...optionProps} key={option.id}>
                    {option.name}
                    {clientLabel ? (
                      <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                        {" - "}
                        {clientLabel}
                      </Typography>
                    ) : null}
                  </li>
                );
              }}
              onChange={projectItem => setProjectId(projectItem.id)}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!projectId || loading}
          variant="contained"
          color="primary"
          onClick={handleTransferAssets}
        >
          Transfer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
